import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';

import style from '../style';
import { fetchDataAgnostic } from 'Services/dataApi';
import { useAuth0 } from 'auth0/reactAuth0Spa';
import { DepartmentContext } from 'DepartmentWrapper';
import { checkLoginForError, formatBearerToken } from 'Common.functions';
const TagFilterPopover = ({ tagFilter, setTagFilter, setShowTagFilter }) => {
  const [tags, setTags] = useState([{ pk_Tag: null, Tag: 'All' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState({
    hasAny: [],
    hasAll: [],
    doesNotHave: [],
  });
  const dContext = useContext(DepartmentContext);
  const { getTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    getTags();
  }, []);

  // Initialize advanced filter from tagFilter if it's an advanced filter
  useEffect(() => {
    if (tagFilter && tagFilter.isAdvanced) {
      setAdvancedFilter({
        hasAny: tagFilter.hasAny || [],
        hasAll: tagFilter.hasAll || [],
        doesNotHave: tagFilter.doesNotHave || [],
      });
      setIsAdvancedMode(true);
    } else {
      // Reset to basic mode if tagFilter is not advanced
      setIsAdvancedMode(false);
    }
  }, [tagFilter]);

  const getTags = () => {
    setIsLoading(true);
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/tags',
          { pk_Department: dContext.department.pk_Department },
          formatBearerToken(token),
        )
          .then((res) => {
            setTags([{ pk_Tag: null, Tag: 'All' }, ...res.data]);
            setIsLoading(false);
          })
          .catch((err) => {
            setIsLoading(false);
          });
      })
      .catch((err) => {
        setIsLoading(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  // Helper function to remove tag from all categories
  const removeTagFromAllCategories = (tagToRemove) => {
    setAdvancedFilter((prev) => ({
      hasAny: prev.hasAny.filter((tag) => tag.pk_Tag !== tagToRemove.pk_Tag),
      hasAll: prev.hasAll.filter((tag) => tag.pk_Tag !== tagToRemove.pk_Tag),
      doesNotHave: prev.doesNotHave.filter((tag) => tag.pk_Tag !== tagToRemove.pk_Tag),
    }));
  };

  // Helper function to add tag to specific category
  const addTagToCategory = (tag, category) => {
    // First remove from all categories to ensure uniqueness
    removeTagFromAllCategories(tag);

    // Then add to the specified category
    setAdvancedFilter((prev) => ({
      ...prev,
      [category]: [...prev[category], tag],
    }));
  };

  // Helper function to remove tag from specific category
  const removeTagFromCategory = (tag, category) => {
    setAdvancedFilter((prev) => ({
      ...prev,
      [category]: prev[category].filter((t) => t.pk_Tag !== tag.pk_Tag),
    }));
  };

  // Get available tags (excluding those already selected in any category)
  const getAvailableTags = () => {
    const usedTagIds = [
      ...advancedFilter.hasAny.map((t) => t.pk_Tag),
      ...advancedFilter.hasAll.map((t) => t.pk_Tag),
      ...advancedFilter.doesNotHave.map((t) => t.pk_Tag),
    ];

    return tags.filter((tag) => tag.pk_Tag !== null && !usedTagIds.includes(tag.pk_Tag));
  };

  // Apply advanced filter
  const applyAdvancedFilter = () => {
    const totalRules = advancedFilter.hasAny.length + advancedFilter.hasAll.length + advancedFilter.doesNotHave.length;

    // If no rules are set, apply the basic "All" filter
    if (totalRules === 0) {
      setTagFilter({ Tag: 'All', pk_Tag: null, isAdvanced: false });
    } else {
      // Create a special filter object that represents the advanced filter
      const advancedFilterObj = {
        isAdvanced: true,
        ...advancedFilter,
        displayText: `Advanced Filter (${totalRules} rules)`,
      };
      setTagFilter(advancedFilterObj);
    }

    setIsAdvancedMode(false);
    setShowTagFilter(false);
  };

  // Clear advanced filter selections (but stay in advanced mode)
  const clearAdvancedFilter = () => {
    setAdvancedFilter({
      hasAny: [],
      hasAll: [],
      doesNotHave: [],
    });
  };

  // Check if current filter is "All" (basic mode with no tag selected)
  const isCurrentFilterAll = () => {
    return !tagFilter.isAdvanced && tagFilter.pk_Tag === null;
  };

  // Check if applying empty advanced filter would be different from current state
  const shouldEnableApplyForEmptyFilter = () => {
    const isEmpty =
      advancedFilter.hasAny.length === 0 &&
      advancedFilter.hasAll.length === 0 &&
      advancedFilter.doesNotHave.length === 0;

    // Enable apply if we have an empty advanced filter but current filter is not "All"
    return isEmpty && !isCurrentFilterAll();
  };

  // Render Basic Search Mode
  const renderBasicMode = () => (
    <div style={{ ...style.simpleColumn, width: '100%', height: '100%' }}>
      {/* Toggle to Advanced Button */}
      <div style={{ padding: '5px', borderBottom: '1px solid #e0e0e0', backgroundColor: 'var(--rr-surface-subtle)', flexShrink: 0 }}>
        <Button color="primary" size="sm" style={{ width: '100%' }} onClick={() => setIsAdvancedMode(true)}>
          Advanced Filtering
        </Button>
      </div>

      {/* Scrollable Tag List */}
      <div style={{ ...style.simpleRow, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <div style={{ ...style.simpleColumn, width: '100%' }}>
          {tags && tags.length > 1 ? (
            tags
              .sort((a, b) => {
                return a.Tag > b.Tag ? 1 : a.Tag < b.Tag ? -1 : 0;
              })
              .map((tag, i) => {
                const backgroundColor = i % 2 == 0 ? 'white' : '#f2f2f2';

                return (
                  <div
                    key={tag.pk_Tag || 'all'}
                    style={{
                      ...style.simpleRow,
                      backgroundColor:
                        (tagFilter && tagFilter.pk_Tag == tag.pk_Tag) || (tag.pk_Tag == null && tagFilter == null)
                          ? '#caecfa'
                          : backgroundColor,
                      padding: '5px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                      flexWrap: 'wrap',
                    }}
                    onClick={() => {
                      setTagFilter(tag);
                      setShowTagFilter(false);
                    }}
                  >
                    {tag.Tag}
                  </div>
                );
              })
          ) : isLoading ? (
            <div
              style={{
                ...style.simpleRow,
                padding: '5px',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              Loading...
            </div>
          ) : (
            <div
              style={{
                ...style.simpleRow,
                padding: '5px',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              No tags found
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Advanced Search Mode
  const renderAdvancedMode = () => (
    <div style={{ ...style.simpleColumn, width: '100%', height: '100%' }}>
      {/* Toggle to Basic Button */}
      <div style={{ padding: '5px', borderBottom: '1px solid #e0e0e0', backgroundColor: 'var(--rr-surface-subtle)', flexShrink: 0 }}>
        <Button color="secondary" size="sm" style={{ width: '100%' }} onClick={() => setIsAdvancedMode(false)}>
          Basic Search
        </Button>
      </div>

      {/* Advanced Filter Content - Scrollable */}
      <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '5px' }}>
        <div style={{ ...style.simpleColumn, gap: '10px' }}>
          {/* Has Any Section */}
          <div>
            <h6 style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '12px' }}>Has Any:</h6>
            <div style={{ ...style.simpleColumn, gap: '3px' }}>
              {advancedFilter.hasAny.map((tag) => (
                <div
                  key={tag.pk_Tag}
                  style={{
                    ...style.simpleRow,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '3px 6px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '3px',
                    fontSize: '11px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={{ cursor: 'pointer', color: '#666', fontSize: '10px', flexShrink: 0 }}
                    onClick={() => removeTagFromCategory(tag, 'hasAny')}
                  />
                </div>
              ))}
              <div style={{ ...style.simpleRow, gap: '2px', flexWrap: 'wrap' }}>
                {getAvailableTags().map((tag) => (
                  <Button
                    key={tag.pk_Tag}
                    size="sm"
                    color="outline-primary"
                    onClick={() => addTagToCategory(tag, 'hasAny')}
                    style={{ margin: '1px', padding: '2px 6px', fontSize: '10px', flexShrink: 0 }}
                  >
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '3px', fontSize: '8px' }} />
                    <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Has All Section */}
          <div>
            <h6 style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '12px' }}>Has All:</h6>
            <div style={{ ...style.simpleColumn, gap: '3px' }}>
              {advancedFilter.hasAll.map((tag) => (
                <div
                  key={tag.pk_Tag}
                  style={{
                    ...style.simpleRow,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '3px 6px',
                    backgroundColor: '#e8f5e8',
                    borderRadius: '3px',
                    fontSize: '11px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={{ cursor: 'pointer', color: '#666', fontSize: '10px', flexShrink: 0 }}
                    onClick={() => removeTagFromCategory(tag, 'hasAll')}
                  />
                </div>
              ))}
              <div style={{ ...style.simpleRow, gap: '2px', flexWrap: 'wrap' }}>
                {getAvailableTags().map((tag) => (
                  <Button
                    key={tag.pk_Tag}
                    size="sm"
                    color="outline-success"
                    onClick={() => addTagToCategory(tag, 'hasAll')}
                    style={{ margin: '1px', padding: '2px 6px', fontSize: '10px', flexShrink: 0 }}
                  >
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '3px', fontSize: '8px' }} />
                    <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Does Not Have Section */}
          <div>
            <h6 style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '12px' }}>Does Not Have:</h6>
            <div style={{ ...style.simpleColumn, gap: '3px' }}>
              {advancedFilter.doesNotHave.map((tag) => (
                <div
                  key={tag.pk_Tag}
                  style={{
                    ...style.simpleRow,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '3px 6px',
                    backgroundColor: '#ffebee',
                    borderRadius: '3px',
                    fontSize: '11px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={{ cursor: 'pointer', color: '#666', fontSize: '10px', flexShrink: 0 }}
                    onClick={() => removeTagFromCategory(tag, 'doesNotHave')}
                  />
                </div>
              ))}
              <div style={{ ...style.simpleRow, gap: '2px', flexWrap: 'wrap' }}>
                {getAvailableTags().map((tag) => (
                  <Button
                    key={tag.pk_Tag}
                    size="sm"
                    color="outline-danger"
                    onClick={() => addTagToCategory(tag, 'doesNotHave')}
                    style={{ margin: '1px', padding: '2px 6px', fontSize: '10px', flexShrink: 0 }}
                  >
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: '3px', fontSize: '8px' }} />
                    <span style={{ wordBreak: 'break-word' }}>{tag.Tag}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ padding: '5px', borderTop: '1px solid #e0e0e0', backgroundColor: 'var(--rr-surface-subtle)', flexShrink: 0 }}>
        <div style={{ ...style.simpleRow, gap: '5px' }}>
          <Button color="secondary" size="sm" onClick={clearAdvancedFilter} style={{ flex: 1 }}>
            Clear All
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={applyAdvancedFilter}
            disabled={
              advancedFilter.hasAny.length === 0 &&
              advancedFilter.hasAll.length === 0 &&
              advancedFilter.doesNotHave.length === 0 &&
              !shouldEnableApplyForEmptyFilter()
            }
            style={{ flex: 2 }}
          >
            Apply Filter
          </Button>
        </div>
      </div>
    </div>
  );

  return isAdvancedMode ? renderAdvancedMode() : renderBasicMode();

  //   return (
  //     <Popover
  //       trigger="legacy"
  //       target="tags_popover"
  //       placementPrefix={'tagFilter bs-popover'}
  //       isOpen={showTagFilter}
  //       toggle={() => setShowTagFilter(!showTagFilter)}
  //     >
  //       <PopoverHeader>
  //         {' '}
  //         <div style={style.spacedBetweenRow}>
  //           <div style={style.buttonLabel}>Tag Filter</div>
  //           <div>
  //             <FontAwesomeIcon
  //               icon={faTimesCircle}
  //               style={style.clickable}
  //               onClick={() => {
  //                 setShowTagFilter(false);
  //               }}
  //             />
  //           </div>
  //         </div>
  //       </PopoverHeader>
  //       <PopoverBody style={{ height: 200 }}>
  //         <div style={style.simpleRow}>
  //           <div style={style.simpleColumn}>
  //             {tags.map((tag, i) => {
  //               const backgroundColor = i % 2 == 0 ? 'white' : '#f2f2f2';

  //               return (
  //                 <div
  //                   style={{
  //                     ...style.simpleRow,
  //                     backgroundColor:
  //                       (tagFilter && tagFilter.pk_Tag == tag.pk_Tag) || (tag.pk_Tag == null && tagFilter == null)
  //                         ? '#caecfa'
  //                         : backgroundColor,
  //                     paddingTop: 5,
  //                     paddingBottom: 5,
  //                     justifyContent: 'center',
  //                     alignItems: 'center',
  //                     cursor: 'pointer',
  //                   }}
  //                   onClick={() => {
  //                     setTagFilter(tag);
  //                     setShowTagFilter(false);
  //                   }}
  //                 >
  //                   {tag.Tag}
  //                 </div>
  //               );
  //             })}
  //           </div>
  //         </div>
  //       </PopoverBody>
  //     </Popover>
  //   );
};

export default TagFilterPopover;
